import React from 'react';
import {AppBar as MuiAppBar, Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, Toolbar, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import Button from '@mui/material/Button';
import SportaLogo from "../../assets/images/sporta-logo.png";
import UserIcon from "../../assets/images/icon_user.svg";
import {useSelector} from 'react-redux';
import LogoutButton from "./LogoutButton";
import {selectGenerationHistoryEnabled, selectUser} from "../../utils/userSlice";
import {useTranslation} from "react-i18next";
import User from "../../models/User";
import {useMobile} from "../../context/MobileContext";
import {useNavigate} from "react-router-dom";
import {useInstallPrompt} from "../../hooks/useInstallPrompt";

// Styled components
const StyledAppBar = styled(MuiAppBar)(({theme}) => ({
    zIndex: theme.zIndex.drawer + 1,
    background: "#05101D",
    boxShadow: "none"
}));

const UserAvatar = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({theme, isMobile}) => ({
    width: isMobile ? 22 : 25,
    height: isMobile ? 22 : 25,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(1),
}));

const UserButton = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({theme, isMobile}) => ({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: isMobile ? `${theme.spacing(0.5)} ${theme.spacing(1)}` : `${theme.spacing(0.75)} ${theme.spacing(2)}`,
    borderRadius: 50,
    height: isMobile ? '30px' : '44px',
    fontSize: isMobile ? '14px' : '16px'
}));

interface AppBarProps {
    // You can add any additional props here if needed
}

const CreationsButton = styled(Button)(({theme}) => ({
    color: 'white',
    textTransform: 'none',
    fontSize: '15px',
    minWidth: 'unset',
    padding: theme.spacing(0.5, 1),
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.1)',
    },
}));

const InstallButton = styled(IconButton)(({theme}) => ({
    color: 'white',
    backgroundColor: 'rgba(255,255,255,0.1)',
    padding: theme.spacing(0.5),
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.2)',
    },
}));

const AppBar: React.FC<AppBarProps> = () => {
    const {t} = useTranslation();
    const {isMobile} = useMobile()
    const navigate = useNavigate()
    const {canInstall, isIOS, showIOSInstructions, setShowIOSInstructions, handleInstallClick} = useInstallPrompt();

    const user = useSelector(selectUser) as User | null;
    const generationHistoryEnabled = useSelector(selectGenerationHistoryEnabled);
    const username = user?.name || t('main.navbar.user');

    function handleLogoClick() {
        navigate('/')
    }

    return (
        <>
            <StyledAppBar position="fixed">
                <Toolbar>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            {/* The logo has always navigated home, but as a bare
                                <img> with no label and a 20px tap target it read
                                as decoration — on a phone there was seemingly no
                                way back to the templates. Same destination, now
                                an actual button: labelled, focusable, and with a
                                finger-sized hit area. */}
                            <Box
                                role="button"
                                tabIndex={0}
                                aria-label={t('main.navbar.backToTemplates')}
                                onClick={handleLogoClick}
                                onKeyDown={(event: React.KeyboardEvent) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        handleLogoClick();
                                    }
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    // A 44px-tall target around a 20px logo:
                                    // the whole area is tappable, not the glyph.
                                    py: 1,
                                    px: 0.5,
                                    borderRadius: 1,
                                    '&:hover': {backgroundColor: 'rgba(255,255,255,0.08)'},
                                }}
                            >
                                <img src={SportaLogo}
                                     alt="Sporta Logo"
                                     style={{
                                         height: isMobile ? 20 : 30,
                                         display: 'block',
                                     }}
                                />
                            </Box>
                        </Grid>
                        <Grid item>
                            <Box display="flex" alignItems="center" gap={{
                                xs: 1,
                                lg: 4
                            }}>
                                {/* Desktop only. On a phone this header is
                                    already logo + greeting + logout, and a
                                    fourth control made it unreadable — the
                                    templates home carries the entry instead. */}
                                {!isMobile && generationHistoryEnabled && (
                                    <CreationsButton
                                        onClick={() => navigate('/creations')}
                                        startIcon={<PhotoLibraryOutlinedIcon/>}
                                        aria-label={t('main.creations.nav')}
                                    >
                                        {t('main.creations.nav')}
                                    </CreationsButton>
                                )}
                                {isMobile && canInstall && (
                                    <InstallButton size="small" onClick={handleInstallClick}>
                                        <GetAppIcon sx={{fontSize: 20}}/>
                                    </InstallButton>
                                )}
                                <UserButton isMobile={isMobile}>
                                    <UserAvatar isMobile={isMobile}>
                                        <img src={UserIcon} style={{width: "100%"}} alt="User Icon"/>
                                    </UserAvatar>
                                    {t('main.navbar.hello')} {username.length > 10 && isMobile ? `${username.slice(0, 10)}...` : username}
                                </UserButton>
                                <LogoutButton/>
                            </Box>
                        </Grid>
                    </Grid>
                </Toolbar>
            </StyledAppBar>

            {isIOS && (
                <Dialog
                    open={showIOSInstructions}
                    onClose={() => setShowIOSInstructions(false)}
                    PaperProps={{sx: {borderRadius: 3, m: 2}}}
                >
                    <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0}}>
                        {t('main.navbar.installApp')}
                        <IconButton size="small" onClick={() => setShowIOSInstructions(false)}>
                            <CloseIcon/>
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, py: 1}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                <Typography variant="body1" fontWeight="bold">1.</Typography>
                                <Typography variant="body2">
                                    {t('main.navbar.iosTapShare')}
                                </Typography>
                                <IosShareIcon sx={{fontSize: 20, color: '#007AFF'}}/>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                <Typography variant="body1" fontWeight="bold">2.</Typography>
                                <Typography variant="body2">
                                    {t('main.navbar.iosAddToHome')}
                                </Typography>
                                <AddBoxOutlinedIcon sx={{fontSize: 20}}/>
                            </Box>
                        </Box>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

export default AppBar;